import { Bot, Check, Copy } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useCreateAgentLink, useRevokeAgentLink } from '../../api/queries'
import type { FavoriteGroup } from '../../api/types'
import { Button } from '../Button'
import { Dialog } from '../Dialog'
import { copyOrShow } from '../ManualCopy'
import { Spinner } from '../Spinner'
import { toast } from '../Toast'

/**
 * «Ссылка для агента»: копирует ссылку на группу. Если её ещё нет — первое
 * нажатие выпускает её, второе («Скопировать ссылку») копирует: после сетевого
 * ожидания WebView (iOS, Telegram) в буфер обмена уже не пускает. «Отозвать»
 * убивает ссылку. Где буфер недоступен, ссылка показывается в листе.
 */
export function AgentLinkActions({ group, wide = false }: { group: FavoriteGroup; wide?: boolean }) {
  const create = useCreateAgentLink()
  const [copied, setCopied] = useState(false)
  // Только что выпущенная ссылка (группа перечитается позже) и ждёт ли она копирования.
  const [fresh, setFresh] = useState<string | null>(null)
  const [armed, setArmed] = useState(false)
  const [revoking, setRevoking] = useState(false)
  const url = group.agent_url ?? fresh

  useEffect(() => {
    if (!copied) return
    const t = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(t)
  }, [copied])

  const onClick = () => {
    if (url) {
      // Копируем сразу в обработчике нажатия, без ожиданий перед этим.
      void copyOrShow(url, { done: 'Ссылка скопирована', title: 'Ссылка для агента' }).then((ok) => {
        setCopied(ok)
        if (ok) setArmed(false)
      })
      return
    }
    create.mutate(group.id, {
      onSuccess: (link) => {
        setFresh(link.url)
        setArmed(true)
        toast('Ссылка выпущена — нажмите «Скопировать ссылку»', { ms: 3500 })
      },
      onError: () => toast('Не получилось выпустить ссылку', { error: true }),
    })
  }

  return (
    <>
      <div className={`flex items-center gap-2 ${wide ? 'w-full' : ''}`}>
        <Button
          variant="primary"
          onClick={onClick}
          disabled={create.isPending}
          className={wide ? 'flex-1' : ''}
          title="Группа одной ссылкой: метрики, подпись, транскрипт и раскадровка каждого поста. Её можно отдать Codex CLI."
        >
          {create.isPending ? (
            <Spinner size={14} className="text-accent-fg" />
          ) : copied ? (
            <Check size={16} />
          ) : armed ? (
            <Copy size={16} />
          ) : (
            <Bot size={16} />
          )}
          {copied ? 'Скопировано' : armed ? 'Скопировать ссылку' : 'Ссылка для агента'}
        </Button>
        {url && (
          <Button
            variant="ghost"
            onClick={() => {
              setFresh(null)
              setArmed(false)
              setRevoking(true)
            }}
          >
            Отозвать
          </Button>
        )}
      </div>
      {revoking && <RevokeDialog group={group} onClose={() => setRevoking(false)} />}
    </>
  )
}

function RevokeDialog({ group, onClose }: { group: FavoriteGroup; onClose: () => void }) {
  const revoke = useRevokeAgentLink()
  return (
    <Dialog
      title="Отозвать ссылку?"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Отмена
          </Button>
          <Button
            variant="danger"
            disabled={revoke.isPending}
            onClick={() =>
              revoke.mutate(group.id, {
                onSuccess: () => {
                  onClose()
                  toast('Ссылка отозвана')
                },
              })
            }
          >
            {revoke.isPending && <Spinner size={14} className="text-white" />}
            Отозвать
          </Button>
        </>
      }
    >
      <p className="text-sm text-pretty">
        Тот, у кого есть ссылка на «{group.name}», больше не сможет забрать группу. Новую ссылку можно выпустить
        в любой момент, кнопкой «Ссылка для агента».
      </p>
      {revoke.isError && <p className="mt-2 text-sm text-danger">Не получилось отозвать.</p>}
    </Dialog>
  )
}
